import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { http, formatApiError } from "@/lib/api";
import { MESSAGE_VARS, renderTemplate } from "@/lib/format";
import { PageHeader, Panel } from "@/components/admin/PageBits";
import { ConfirmationCard } from "@/components/portal/ConfirmationStep";
import { ProgressStagesEditor } from "@/components/admin/ProgressStagesEditor";

const SAMPLE = { nome: "Maria Souza Lima", cnpj: "11.222.333/0001-81", protocolo: "REW-20260101-A1B2C3", horario: "Manhã (9h-12h)" };

export default function MessagesPage() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState("content");
  const refs = { title: useRef(null), content: useRef(null) };

  const apply = (s) => { setTitle(s.message.title); setContent(s.message.content); };
  useEffect(() => { http.get("/admin/settings").then(({ data }) => apply(data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const insertVar = (v) => {
    const el = refs[focus].current;
    const [val, setter] = focus === "title" ? [title, setTitle] : [content, setContent];
    const start = el?.selectionStart ?? val.length;
    const end = el?.selectionEnd ?? val.length;
    setter(val.slice(0, start) + v + val.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + v.length, start + v.length); });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put("/admin/settings", { message: { title, content } });
      apply(data);
      toast.success("Mensagem salva.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const reset = async () => {
    try {
      const { data } = await http.post("/admin/settings/message/reset");
      apply(data);
      toast.success("Mensagem padrão restaurada.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Mensagens de atendimento" subtitle="Configurações · Mensagem exibida ao cliente após solicitar o agendamento.">
        <div className="flex gap-2">
          <button onClick={reset} className="btn-ghost-sm" data-testid="reset-messages-btn"><RotateCcw className="h-3.5 w-3.5" /> Restaurar padrão</button>
          <button onClick={save} disabled={busy || !title.trim() || !content.trim()} className="btn-gold h-9 px-4 text-sm" data-testid="save-messages-btn"><Save className="h-4 w-4" /> Salvar</button>
        </div>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Editor">
          <label className="mb-1 block text-xs text-slate-400">Título</label>
          <Input ref={refs.title} value={title} maxLength={150} onFocus={() => setFocus("title")} onChange={(e) => setTitle(e.target.value)} className="border-white/10 bg-black/30 text-slate-100" data-testid="message-title-input" />
          <label className="mb-1 mt-4 block text-xs text-slate-400">Conteúdo</label>
          <Textarea ref={refs.content} value={content} maxLength={3000} onFocus={() => setFocus("content")} onChange={(e) => setContent(e.target.value)} className="min-h-[220px] border-white/10 bg-black/30 text-slate-100" data-testid="message-content-input" />
          <p className="mb-2 mt-4 text-xs text-slate-400">Variáveis (clique para inserir no campo selecionado):</p>
          <div className="flex flex-wrap gap-2">
            {MESSAGE_VARS.map((v) => (
              <button key={v} onClick={() => insertVar(v)} className="rounded-full border px-3 py-1 font-mono-x text-xs transition-colors hover:bg-white/5" style={{ borderColor: "var(--brand)", color: "var(--brand)" }} data-testid={`var-chip-${v.slice(1, -1)}`}>{v}</button>
            ))}
          </div>
        </Panel>
        <Panel title="Pré-visualização do cliente" testId="message-preview">
          <div className="portal-bg rounded-md p-4 sm:p-6">
            <ConfirmationCard title={renderTemplate(title, SAMPLE)} content={renderTemplate(content, SAMPLE)} protocolo={SAMPLE.protocolo} />
          </div>
          <p className="mt-3 text-xs text-slate-500">Dados de exemplo usados apenas na pré-visualização.</p>
        </Panel>
      </div>
      <ProgressStagesEditor />
    </div>
  );
}
